import React, { useState, useCallback } from 'react';
import { Menu, X, LogOut, ChevronDown, LayoutDashboard, FileText, ChevronRight } from 'lucide-react';
import './DashboardLayout.css';

/**
 * Professional Dashboard Layout
 * Complete navbar + sidebar with hardcoded menus
 */

function DashboardLayout({ user, onLogout, children, onTabChange, userRole = 'admin' }) {
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [expandCMS, setExpandCMS] = useState(false); // Closed by default
  const [expandedMenus, setExpandedMenus] = useState({}); // All collapsed by default
  const [activeTab, setActiveTab] = useState('dashboard');

  const handleLogout = useCallback(() => {
    setDropdownOpen(false);
    onLogout();
    setTimeout(() => {
      window.location.href = 'https://permataharapansecondary.sch.id/';
    }, 100);
  }, [onLogout]);

  const getUserInitials = useCallback(() => {
    if (!user?.full_name) return 'U';
    return user.full_name
      .split(' ')
      .map(n => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  }, [user?.full_name]);

  const handleTabChange = useCallback((tabId) => {
    setActiveTab(tabId);
    if (onTabChange) {
      onTabChange(tabId);
    }
  }, [onTabChange]);

  // Hardcoded menus with all sections
  const MENUS = [
    {
      id: 'homepage',
      title: 'Homepage',
      slug: 'homepage',
      sections: [
        { id: 'cms-hero', title: 'Hero Section', slug: 'hero' },
        { id: 'cms-quicklinks', title: 'Quick Links', slug: 'quicklinks' },
        { id: 'cms-highlights', title: 'Highlights', slug: 'highlights' },
        { id: 'cms-news', title: 'News', slug: 'news' },
        { id: 'cms-countdown', title: 'Countdown', slug: 'countdown' }
      ]
    },
    {
      id: 'about-us',
      title: 'About Us',
      slug: 'about-us',
      sections: [
        { id: 'cms-about-hero', title: 'Hero Section', slug: 'hero' },
        { id: 'cms-about-principal', title: 'Principal Message', slug: 'principal' },
        { id: 'cms-about-values', title: 'Vision Mission Values', slug: 'values' },
        { id: 'cms-about-philosophy', title: 'Philosophy', slug: 'philosophy' },
        { id: 'cms-about-history', title: 'History', slug: 'history' },
        { id: 'cms-about-leadership', title: 'Leadership', slug: 'leadership' }
      ]
    },
    {
      id: 'education-program',
      title: 'Education Program',
      slug: 'education-program',
      sections: [
        { id: 'cms-education-hero', title: 'Hero Section', slug: 'hero' },
        { id: 'cms-edu-programs', title: 'Program Levels', slug: 'programs' },
        { id: 'cms-edu-curriculum', title: 'Curriculum', slug: 'curriculum' },
        { id: 'cms-edu-subjects', title: 'Subjects', slug: 'subjects' },
        { id: 'cms-edu-learning', title: 'Learning Approach', slug: 'learning' },
        { id: 'cms-edu-assessment', title: 'Assessment', slug: 'assessment' },
        { id: 'cms-edu-special-programs', title: 'Special Programs', slug: 'special-programs' }
      ]
    },
    {
      id: 'admissions',
      title: 'Admissions',
      slug: 'admissions',
      sections: [
        { id: 'cms-adm-hero', title: 'Hero Section', slug: 'hero' },
        { id: 'cms-adm-levels', title: 'Admission Levels', slug: 'levels' },
        { id: 'cms-adm-process', title: 'Application Process', slug: 'process' },
        { id: 'cms-adm-requirements', title: 'Requirements', slug: 'requirements' },
        { id: 'cms-adm-timeline', title: 'Key Dates', slug: 'timeline' },
        { id: 'cms-adm-fees', title: 'Fees & Payment', slug: 'fees' },
        { id: 'cms-adm-online-app', title: 'Online Application', slug: 'online-app' }
      ]
    },
    {
      id: 'student-life',
      title: 'Student Life',
      slug: 'student-life',
      sections: [
        { id: 'cms-life-hero', title: 'Hero Section', slug: 'hero' },
        { id: 'cms-life-clubs', title: 'Clubs & ECA', slug: 'clubs' },
        { id: 'cms-life-events', title: 'Events & Competitions', slug: 'events' },
        { id: 'cms-life-house-system', title: 'House System', slug: 'house-system' },
        { id: 'cms-life-wellbeing', title: 'Wellbeing & SEL', slug: 'wellbeing' },
        { id: 'cms-life-character-shaping', title: 'Character Shaping', slug: 'character-shaping' }
      ]
    },
    {
      id: 'facilities',
      title: 'Facilities',
      slug: 'facilities',
      sections: [
        { id: 'cms-fac-hero', title: 'Hero Section', slug: 'hero' },
        { id: 'cms-fac-list', title: 'Facility List', slug: 'list' },
        { id: 'cms-fac-gallery', title: 'Gallery', slug: 'gallery' }
      ]
    },
    {
      id: 'news-updates',
      title: 'News & Updates',
      slug: 'news-updates',
      sections: [
        { id: 'cms-news-hero', title: 'Hero Section', slug: 'hero' },
        { id: 'cms-news-list', title: 'News Articles', slug: 'articles' },
        { id: 'cms-news-events', title: 'Events', slug: 'events' }
      ]
    },
    {
      id: 'parents',
      title: 'Parents',
      slug: 'parents',
      sections: [
        { id: 'cms-par-hero', title: 'Hero Section', slug: 'hero' },
        { id: 'cms-par-info', title: 'Parent Information', slug: 'info' },
        { id: 'cms-par-forms', title: 'Forms & Documents', slug: 'forms' }
      ]
    },
    {
      id: 'careers',
      title: 'Careers',
      slug: 'careers',
      sections: [
        { id: 'cms-car-hero', title: 'Hero Section', slug: 'hero' },
        { id: 'cms-car-openings', title: 'Job Openings', slug: 'openings' },
        { id: 'cms-car-culture', title: 'Our Culture', slug: 'culture' }
      ]
    }
  ];

  const toggleMenuExpanded = useCallback((menuId) => {
    setExpandedMenus(prev => ({
      ...prev,
      [menuId]: !prev[menuId]
    }));
  }, []);

  // Menu items based on role
  const showContentManagement = userRole === 'website_admin' || userRole === 'super_admin';
  const showApprovalQueue = userRole === 'principal' || userRole === 'super_admin';

  return (
    <div className="dashboard-layout">
      {/* NAVBAR */}
      <nav className="dashboard-navbar">
        <div className="navbar-left">
          <button
            className="toggle-btn"
            onClick={() => setSidebarOpen(!sidebarOpen)}
          >
            {sidebarOpen ? (
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <line x1="18" y1="6" x2="6" y2="18"></line>
                <line x1="6" y1="6" x2="18" y2="18"></line>
              </svg>
            ) : (
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <line x1="3" y1="6" x2="21" y2="6"></line>
                <line x1="3" y1="12" x2="21" y2="12"></line>
                <line x1="3" y1="18" x2="21" y2="18"></line>
              </svg>
            )}
          </button>
          <div className="logo-group">
            <img src="/logo_PHSchool.png" alt="Logo" className="logo" />
            <div className="brand">
              <div className="school-name">Permata Harapan</div>
              <div className="school-subtitle">Secondary</div>
            </div>
          </div>
        </div>

        <div className="navbar-right">
          <div className="user-card">
            <div className="avatar">{getUserInitials()}</div>
            <div className="user-text">
              <div className="name">{user?.full_name || 'User'}</div>
              <div className="email">{user?.email || 'user@school.id'}</div>
            </div>
          </div>

          <div className="dropdown">
            <button className="dropdown-btn" onClick={() => setDropdownOpen(!dropdownOpen)}>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="6 9 12 15 18 9"></polyline>
              </svg>
            </button>
            {dropdownOpen && (
              <div className="dropdown-menu">
                <button className="dropdown-item logout" onClick={handleLogout}>
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"></path>
                    <polyline points="16 17 21 12 16 7"></polyline>
                    <line x1="21" y1="12" x2="9" y2="12"></line>
                  </svg>
                  Logout
                </button>
              </div>
            )}
          </div>
        </div>
      </nav>

      {/* MAIN CONTENT */}
      <div className="dashboard-body">
        {/* SIDEBAR */}
        <aside className={`sidebar ${sidebarOpen ? 'open' : 'closed'}`}>
          <nav className="sidebar-nav">
            <button
              className={`nav-btn ${activeTab === 'dashboard' ? 'active' : ''}`}
              onClick={() => handleTabChange('dashboard')}
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <rect x="3" y="3" width="7" height="7"></rect>
                <rect x="14" y="3" width="7" height="7"></rect>
                <rect x="14" y="14" width="7" height="7"></rect>
                <rect x="3" y="14" width="7" height="7"></rect>
              </svg>
              <span>Dashboard</span>
            </button>

            {showContentManagement && (
              <div className="nav-section">
                <button
                  className={`nav-btn collapsible ${expandCMS ? 'expanded' : ''}`}
                  onClick={() => setExpandCMS(!expandCMS)}
                >
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V10z"></path>
                    <polyline points="14 2 14 10 22 10"></polyline>
                    <line x1="12" y1="19" x2="12" y2="5"></line>
                    <line x1="9" y1="16" x2="15" y2="16"></line>
                  </svg>
                  <span>Content Management</span>
                  <ChevronRight size={16} className={`chevron ${expandCMS ? 'expanded' : ''}`} />
                </button>

                {expandCMS && (
                  <div className="cms-submenu">
                    {MENUS.map(menu => (
                      <div key={menu.id}>
                        <button
                          className={`cms-submenu-btn ${expandedMenus[menu.id] ? 'expanded' : ''}`}
                          onClick={() => toggleMenuExpanded(menu.id)}
                        >
                          <span>{menu.title}</span>
                          <ChevronRight size={16} className={`chevron ${expandedMenus[menu.id] ? 'expanded' : ''}`} />
                        </button>

                        {expandedMenus[menu.id] && (
                          <div className="homepage-sections">
                            {menu.sections.map(section => (
                              <button
                                key={section.id}
                                className={`section-item ${activeTab === section.id ? 'active' : ''}`}
                                onClick={() => handleTabChange(section.id)}
                              >
                                {section.title}
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {showApprovalQueue && (
              <div className="nav-section">
                <button
                  className={`nav-btn ${activeTab === 'approvals' ? 'active' : ''}`}
                  onClick={() => handleTabChange('approvals')}
                >
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="20 6 9 17 4 12"></polyline>
                  </svg>
                  <span>Submissions</span>
                </button>
              </div>
            )}
          </nav>
        </aside>

        {/* CONTENT */}
        <main className="dashboard-content">
          {children}
        </main>
      </div>
    </div>
  );
}

export default DashboardLayout;
